import React, { useState, useEffect } from 'react';
import PageMeta from '../components/PageMeta';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, MapPin, Send, MessageSquare, Shield, CheckCircle, Users, Award } from 'lucide-react';
import { mockData } from '../mock';
import { toast } from 'sonner';
import { submitFormWithGeoLocation } from '../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const result = await submitFormWithGeoLocation(formData);

      if (result.success) {
        toast.success(result.message || 'Thank you for contacting us! We will get back to you soon.');
        setFormData({ name: '', email: '', countryCode: '+1', contactNumber: '', message: '' });
      } else {
        toast.error(result.error || 'Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen">
      <PageMeta
        title="Contact Us"
        description="Get in touch with Global Trademark Federation for trademark registration, copyright services, and brand protection. Our experts are ready to help secure your intellectual property."
      />
      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center pt-36 pb-28 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.90), rgba(45, 48, 71, 0.80)), url(${mockData.heroImages.contact})`,
        }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4em0tMTYgMGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4eiIvPjwvZz48L2c+PC9zdmc+')] opacity-20" />
        <div className="container mx-auto px-4 text-center text-white relative z-10">
          <div className="inline-block mb-4">
            <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold border border-white/30">
              Get In Touch
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-95 leading-relaxed">
            Get in touch with our expert team for trademark registration assistance
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-gray-200 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-gray-200 rounded-full blur-3xl opacity-30" />
        
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <div className="mb-8">
                <div className="inline-block mb-4">
                  <span className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Contact Information
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-primary to-gray-900 bg-clip-text text-transparent mb-4">Let's Connect</h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Have questions about trademark registration? Our team is here to help. Reach out to us using any of the methods below.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="border-2 border-l-4 border-l-secondary hover:border-secondary/50 transition-all hover:shadow-lg group">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-gradient-to-br from-secondary/10 to-accent/20 w-14 h-14 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Mail className="w-7 h-7 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1 text-lg">Email</h3>
                      <a href={`mailto:${mockData.contact.email}`} className="text-secondary hover:text-secondary/80 font-medium">
                        {mockData.contact.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-l-4 border-l-secondary hover:border-secondary/50 transition-all hover:shadow-lg group">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-gradient-to-br from-accent/20 to-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Phone className="w-7 h-7 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1 text-lg">Phone</h3>
                      <a href={`tel:${mockData.contact.phone}`} className="text-secondary hover:text-secondary/80 font-medium">
                        {mockData.contact.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-l-4 border-l-secondary hover:border-secondary/50 transition-all hover:shadow-lg group">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-gradient-to-br from-secondary/10 to-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 transition-transform">
                      <MapPin className="w-7 h-7 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1 text-lg">Head Office</h3>
                      <p className="text-gray-600 font-medium">{mockData.contact.headOffice}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-l-4 border-l-secondary hover:border-secondary/50 transition-all hover:shadow-lg group">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-gradient-to-br from-secondary/10 to-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 transition-transform">
                      <MapPin className="w-7 h-7 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1 text-lg">Corporate Building</h3>
                      <p className="text-gray-600 font-medium">{mockData.contact.corporateBuilding}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="shadow-2xl border-2 hover:border-secondary/50 transition-all">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-gradient-to-br from-secondary/10 to-accent/20 w-12 h-12 rounded-2xl flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-secondary" />
                      </div>
                      <h2 className="text-3xl font-bold text-primary">Send Us a Message</h2>
                    </div>
                    <p className="text-gray-600">Fill out the form below and we'll get back to you within 24 hours.</p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <Label htmlFor="name" className="font-semibold">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                        placeholder="Your full name"
                        className="mt-1 border-2 focus:border-secondary"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="font-semibold">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        placeholder="your.email@example.com"
                        className="mt-1 border-2 focus:border-secondary"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="contact" className="font-semibold">Contact Number *</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                          <SelectTrigger className="w-[140px] border-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {mockData.countryCodes.map((item) => (
                              <SelectItem key={item.code} value={item.code}>
                                {item.code} {item.country}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          id="contact"
                          type="tel"
                          value={formData.contactNumber}
                          onChange={(e) => handleChange('contactNumber', e.target.value)}
                          required
                          placeholder="123-456-7890"
                          className="flex-1 border-2 focus:border-secondary"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="message" className="font-semibold">Message</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="Tell us how we can help..."
                        className="mt-1 min-h-[140px] border-2 focus:border-secondary"
                      />
                    </div>
                    
                    <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-semibold py-6 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                      <Send className="w-5 h-5 mr-2" />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-4 py-2 rounded-full text-sm font-semibold">
                Why Trust Us
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-primary">Trusted by Thousands of Businesses</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Card className="text-center border-2 hover:border-secondary/50 hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-secondary mb-1">10,000+</h3>
                <p className="text-gray-600 font-medium">Trademarks Registered</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-secondary/50 hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-secondary mb-1">98%</h3>
                <p className="text-gray-600 font-medium">Success Rate</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-secondary/50 hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-secondary mb-1">50+</h3>
                <p className="text-gray-600 font-medium">Expert Attorneys</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-secondary/50 hover:shadow-lg transition-all hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-secondary mb-1">15+</h3>
                <p className="text-gray-600 font-medium">Years of Experience</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;